// src/resources/dashboards/Painel360/index.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { useDataProvider, useGetList, useGetOne, useNotify, usePermissions } from 'react-admin';
import { PERMISSIONS } from '../../../constants';
import { getChainId, getChainName, getPlaceId } from '../../../lib';
import { track, EVENTS } from '../../../lib/analytics';
import ManualLinkButton from '../../../manual/components/ManualLinkButton';
import { COLORS, Section, useDashboardSection } from './shared';
import {
  DashboardFilter, EngagementData, InsightsData, LoyaltyData,
  OverviewData, PenetrationData, PointsEconomyData, RedemptionsData,
} from './types';
import EngagementSection from './EngagementSection';
import RedemptionsSection from './RedemptionsSection';
import InsightsSection from './InsightsSection';
import LoyaltySection from './LoyaltySection';
import PenetrationSection from './PenetrationSection';
import PointsEconomySection from './PointsEconomySection';
import PulseSection from './PulseSection';

type Preset = 'today' | '7d' | '30d' | '90d' | 'custom';

const PRESETS: { key: Preset; label: string; days?: number }[] = [
  { key: 'today', label: 'Hoje', days: 0 },
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
  { key: 'custom', label: 'Personalizado' },
];

// Humanizador pt-BR ("há X") — plugin relativeTime do dayjs não é carregado globalmente,
// então fazemos um cálculo simples local pra não puxar plugin global.
const humanizeAgo = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = dayjs(iso);
  if (!then.isValid()) return null;
  const seconds = Math.max(0, dayjs().diff(then, 'second'));
  if (seconds < 60) return 'agora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
};

const Painel360 = () => {
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const chainId = getChainId();
  const chainName = getChainName();
  const placeId = getPlaceId();

  const [refreshing, setRefreshing] = useState(false);
  const [preset, setPreset] = useState<Preset>('30d');
  const [from, setFrom] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [stationIds, setStationIds] = useState<string[]>(placeId ? [placeId] : []);

  const { data: stations } = useGetList('stations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  useEffect(() => { track(EVENTS.DASHBOARD_OPENED, { dashboard: 'dashboard-v2' }); }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    const def = PRESETS.find(x => x.key === p);
    if (def?.days !== undefined) {
      setFrom(dayjs().subtract(def.days, 'day').format('YYYY-MM-DD'));
      setTo(dayjs().format('YYYY-MM-DD'));
    }
  };

  const filter: DashboardFilter = useMemo(() => ({
    from,
    to: dayjs(to).add(1, 'day').format('YYYY-MM-DD'), // `to` exclusivo no backend; inclui o dia selecionado inteiro
    ...(stationIds.length ? { stationIds } : {}),
  }), [from, to, stationIds]);

  const enabled = !!chainId;
  // Botão "criar regra de recuperação" só aparece para redes com o recurso habilitado (ChainEdit:
  // useCustomerRecovery) E pra quem tem a permissão da tela de campanhas — antes só olhava a flag
  // da rede, então um funcionário sem a permissão via o botão mesmo sem poder usar a tela que ele abre.
  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled });
  const usesCustomerRecovery = chain?.useCustomerRecovery === true
    && !!permissions?.includes(PERMISSIONS.CUSTOMER_RECOVERY_RULES);
  const insights = useDashboardSection<InsightsData>('dashboard-v2/insights', filter, enabled);
  const overview = useDashboardSection<OverviewData>('dashboard-v2/overview', filter, enabled);
  const loyalty = useDashboardSection<LoyaltyData>('dashboard-v2/loyalty', filter, enabled);
  const points = useDashboardSection<PointsEconomyData>('dashboard-v2/points-economy', filter, enabled);
  const penetration = useDashboardSection<PenetrationData>('dashboard-v2/penetration', filter, enabled);
  const engagement = useDashboardSection<EngagementData>('dashboard-v2/engagement', filter, enabled);
  const redemptions = useDashboardSection<RedemptionsData>('dashboard-v2/redemptions', filter, enabled);

  // Fidelidade e pontos vêm do mesmo rollup pré-agregado — usa loyalty como primário, points como fallback.
  const computedAt = loyalty.data?.computedAt ?? points.data?.computedAt ?? null;
  const freshnessLabel = humanizeAgo(computedAt);

  // Quando o rollup está sendo construído pela 1ª vez (backend devolve computing:true e dados vazios/null),
  // faz auto-poll a cada 20s até as duas seções pararem de computar. Deps = flags booleanas (NÃO o objeto data)
  // pra evitar re-render em loop. Limpa o timer quando computing vira false ou no unmount.
  const isComputing = loyalty.data?.computing === true || points.data?.computing === true;
  const loyaltyRefetch = loyalty.refetch;
  const pointsRefetch = points.refetch;
  useEffect(() => {
    if (!isComputing) return;
    const timer = setInterval(() => {
      loyaltyRefetch();
      pointsRefetch();
    }, 20000);
    return () => clearInterval(timer);
  }, [isComputing, loyaltyRefetch, pointsRefetch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Recompute do rollup é NÃO-bloqueante no backend: retorna na hora com computing:true e roda em background.
      await dataProvider.create('dashboard-v2/refresh', { data: {} });
      // Só fidelidade e pontos usam o rollup; recarrega as duas — elas voltam com computing:true,
      // o que dispara o auto-poll (useEffect em isComputing) até o rollup ficar pronto.
      await Promise.all([loyalty.refetch(), points.refetch()]);
    } catch {
      notify('Não foi possível atualizar agora, tente de novo.', { type: 'warning' });
    } finally {
      setRefreshing(false);
    }
  };

  if (permissionsLoading) return null;

  if (!permissions?.includes(PERMISSIONS.DASHBOARD_V2)) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">Acesso não autorizado.</Typography>
      </Box>
    );
  }

  if (!chainId) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: COLORS.ink }}>Selecione uma rede para ver o Painel 360</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Como administrador, escolha uma rede no menu Cadastro → Rede e tente novamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', mx: -1, pb: 8, background: 'linear-gradient(145deg, #f0f4ff 0%, #f8fafc 45%, #f5f0ff 100%)' }}>
      {/* Header sticky full-width; conteúdo num wrap centralizado (maxWidth 1240) */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 30, mb: 1,
        backdropFilter: 'blur(14px)', background: 'rgba(248,250,252,0.85)',
        borderBottom: '1px solid rgba(120,105,223,0.10)',
      }}>
        <Box sx={{
          maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 3 }, py: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5,
        }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{
              fontSize: 26, fontWeight: 800,
              background: `linear-gradient(90deg, ${COLORS.brand2}, ${COLORS.brand}, #275030)`,
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Painel 360
            </Typography>
            <Box component="span" sx={{
              fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#fff',
              background: `linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`,
              borderRadius: 999, px: 1.1, py: 0.4,
            }}>NOVO</Box>
            <ManualLinkButton slug="dashboard-v2" label="Como funciona" />
          </Box>
          <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
            Fidelidade, receita e engajamento da sua rede em um só lugar
            {chainName ? ` · ${chainName}` : ''} · comparando com o período anterior
          </Typography>
          {isComputing ? (
            <Typography sx={{ fontSize: 11.5, color: COLORS.brand, fontWeight: 600, mt: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} sx={{ color: COLORS.brand }} />
              Preparando os dados de fidelidade e pontos pela primeira vez… isso leva alguns minutos.
            </Typography>
          ) : refreshing ? (
            <Typography sx={{ fontSize: 11.5, color: COLORS.brand, fontWeight: 600, mt: 0.25 }}>
              Atualizando fidelidade e pontos… isso pode levar alguns instantes.
            </Typography>
          ) : freshnessLabel ? (
            <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 0.25 }}>
              Fidelidade e pontos atualizados {freshnessLabel}
            </Typography>
          ) : computedAt === null && (loyalty.loading || points.loading) ? (
            <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 0.25 }}>
              Calculando fidelidade e pontos…
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {PRESETS.map(p => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={preset === p.key ? {
                fontWeight: 700, color: '#fff',
                background: `linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`,
              } : { fontWeight: 600, bgcolor: '#fff' }}
            />
          ))}
          {preset === 'custom' && (
            <>
              <TextField type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} sx={{ width: 150, bgcolor: '#fff' }} />
              <TextField type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                inputProps={{ max: dayjs().format('YYYY-MM-DD') }} sx={{ width: 150, bgcolor: '#fff' }} />
            </>
          )}
          {!placeId && (
            <Autocomplete
              multiple size="small" limitTags={1} disableCloseOnSelect
              options={stations ?? []}
              getOptionLabel={(o: any) => o.name ?? o.id}
              value={(stations ?? []).filter((s: any) => stationIds.includes(s.id))}
              onChange={(_, values: any[]) => setStationIds(values.map(v => v.id))}
              // Pill compacto, mesma vibe dos chips de período. bgcolor SÓ no input
              // (não no root) — root branco criava aquela caixa sobrando em cima.
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': { borderRadius: 999, bgcolor: '#fff', py: '2px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120,105,223,0.25)', top: 0 },
                '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.brand },
              }}
              renderInput={params => (
                <TextField {...params} placeholder={stationIds.length ? '' : `🏪 Todos os postos${stations?.length ? ` (${stations.length})` : ''}`} />
              )}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={
              refreshing
                ? <CircularProgress size={15} sx={{ color: COLORS.brand }} />
                : <RefreshIcon fontSize="small" />
            }
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#fff',
              borderColor: 'rgba(120,105,223,0.35)', color: COLORS.brand,
              '&:hover': { borderColor: COLORS.brand, bgcolor: 'rgba(120,105,223,0.06)' },
            }}
          >
            {refreshing ? 'Atualizando…' : 'Atualizar'}
          </Button>
        </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 3 } }}>
        <Section index={0}><InsightsSection state={insights} /></Section>
        <Section index={1}><PulseSection state={overview} /></Section>
        <Section index={2}><LoyaltySection state={loyalty} canCreateRecovery={usesCustomerRecovery} stationIds={stationIds} /></Section>
        <Section index={3}><PointsEconomySection state={points} /></Section>
        <Section index={4}><RedemptionsSection state={redemptions} /></Section>
        <Section index={5}><PenetrationSection state={penetration} from={from} to={to} stationIds={stationIds} chainId={chainId} /></Section>
        <Section index={6}><EngagementSection state={engagement} from={from} to={to} chainId={chainId} stationIds={stationIds} /></Section>
      </Box>
    </Box>
  );
};

export default Painel360;
